import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleMobileHomeVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "rent-a-mobilehome-in-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext() || { articles: [] };
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "types-of-mobile-homes": "Types of Mobile Homes",
    "who-is-this-guide-for": "Who Is This Guide For?",
    budget: "Budget Considerations",
    "best-locations": "Best Locations for Renting a Mobile Home",
    "local-regulations": "Local Regulations & Best Practices",
    "seasonal-availability": "Seasonal Availability & Considerations",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vancouver.ca/streets-transportation/parking.aspx",
      label: "City of Vancouver – Parking Regulations for Large Vehicles",
    },
    {
      url: "https://www.peopleslawschool.ca/public-legal-information/living-in-a-mobile-home-park-bc/",
      label: "People’s Law School: Living in a Mobile Home Park (BC)",
    },
    {
      url: "https://www.travel-british-columbia.com",
      label: "Travel British Columbia – RV Parks & Camping Directory",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // We have 5 best locations with unique coordinates
  // Make sure IDs match the relevant section anchor
  const locations = [
    {
      id: "best-locations-1", 
      lat: 49.32421, 
      lng: -123.13213,
      popupText: "Capilano River RV Park (West Vancouver)",
    },
    {
      id: "best-locations-2", 
      lat: 49.24823, 
      lng: -122.91238,
      popupText: "Burnaby Cariboo RV Park (Burnaby)",
    },
    {
      id: "best-locations-3",
      lat: 49.20817, 
      lng: -122.80326,
      popupText: "Dogwood Campgrounds & RV Park (Surrey)",
    },
    {
      id: "best-locations-4",
      lat: 49.07505, 
      lng: -122.81369,
      popupText: "Peace Arch RV Park (South Surrey)",
    },
    {
      id: "best-locations-5",
      lat: 49.17488, 
      lng: -122.57763,
      popupText: "Fort Camping (Brae Island, Fort Langley)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Ultimate Guide to Renting a Mobile Home in Vancouver</title>
        <meta
          name="description"
          content="Learn how to rent a mobile home in Vancouver with actionable steps, best locations, local regulations, FAQs, and more."
        />
        <meta
          name="keywords"
          content="Vancouver mobile home, RV rental, tiny homes, best RV parks, mobile home regulations"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/rent-a-mobilehome-in-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Ultimate Guide to Renting a Mobile Home in Vancouver"
        />
        <meta
          property="og:description"
          content="Detailed information on renting mobile homes, RVs, tiny houses, and more in Vancouver."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/rent-a-mobilehome-in-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/rent-a-mobilehome-in-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Ultimate Guide to Renting a Mobile Home in Vancouver"
        />
        <meta
          name="twitter:description"
          content="Actionable tips, locations, and regulations for Vancouver mobile home living."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/rent-a-mobilehome-in-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Mobile Home in Vancouver setting"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s stunning coastal setting, lush forests, and vibrant
              city life make it a coveted destination for both long-term
              residents and short-term travelers. However, with housing costs on
              the rise, many people are turning to mobile homes—RVs, tiny homes,
              and more—for a flexible and affordable alternative. Whether you’re
              a digital nomad looking for a scenic office on wheels or a new
              resident seeking a cost-effective landing spot, this guide will
              walk you through everything you need to know about renting a
              mobile home in Vancouver.
            </p>

            {/* SECTION: Types of Mobile Homes */}
            <h2 id="types-of-mobile-homes">Types of Mobile Homes Available</h2>
            <p>
              In Vancouver, you’ll encounter a range of mobile home options—from
              fully equipped RVs designed for travel to stationary modular homes
              set up in designated parks. Each type has pros and cons:
            </p>
            <ul>
              <li>
                <strong>Recreational Vehicles (RVs)</strong>: Ideal for
                short-term trips or frequent moves. They come equipped with
                kitchens, beds, and bathrooms but may have limited living space.
                Fuel and maintenance can add up.
              </li>
              <li>
                <strong>Tiny Homes on Wheels</strong>: Built on trailer frames
                but designed like a small house. They offer better insulation
                and a homey atmosphere but are harder to move frequently.
              </li>
              <li>
                <strong>Modular/Manufactured Homes</strong>: Larger, more
                spacious, often installed in mobile home parks for long-term
                stays. Less mobile, but typically more comfortable for extended
                living.
              </li>
              <li>
                <strong>Long-Term Mobile Home Rentals</strong>: RVs or tiny
                homes rented monthly. Great for temporary workers or people
                exploring living in Vancouver without buying or signing a long
                apartment lease.
              </li>
            </ul>

            {/* SECTION: Who Is This Guide For */}
            <h2 id="who-is-this-guide-for">Who Is This Guide For?</h2>
            <p>
              Anyone curious about mobile-home living in Vancouver can benefit
              from this guide:
            </p>
            <ul>
              <li>
                <strong>Tourists &amp; Travelers:</strong> Rent an RV and take
                advantage of Vancouver’s many campgrounds, enjoying easy access
                to beaches, mountains, and downtown attractions.
              </li>
              <li>
                <strong>Digital Nomads &amp; Remote Workers:</strong> Live and
                work from a mobile home with modern amenities, enjoying
                high-speed Wi-Fi in scenic RV parks.
              </li>
              <li>
                <strong>Temporary Workers &amp; New Residents:</strong> Get a
                flexible, fully furnished place without the commitment of a
                long-term rental contract.
              </li>
              <li>
                <strong>Retirees &amp; Snowbirds:</strong> Enjoy Vancouver’s
                mild summer climate in an RV or mobile home park and relocate to
                warmer spots in the off-season.
              </li>
            </ul>

            {/* SECTION: Budget Considerations */}
            <h2 id="budget">Budget Considerations</h2>
            <p>
              Renting a mobile home can save costs compared to traditional
              rentals, but remember to factor in:
            </p>
            <ul>
              <li>
                <strong>Rental Rates:</strong> RVs can range from CA$50–$300+ a
                night, depending on size and season. Monthly pad fees in parks
                can run from CA$350–$700+.
              </li>
              <li>
                <strong>Campground/Parking Fees:</strong> Expect CA$30–$60 per
                night at private RV parks, which may include utilities.
              </li>
              <li>
                <strong>Utilities:</strong> Electricity, water, propane, and
                Wi-Fi may add to monthly costs.
              </li>
              <li>
                <strong>Insurance &amp; Deposits:</strong> RV rentals usually
                require a security deposit, and if driving, you’ll need proper
                insurance coverage.
              </li>
            </ul>

            {/* SECTION: Best Locations for Renting a Mobile Home */}
            <h2 id="best-locations">Best Locations for Renting a Mobile Home</h2>
            <p>
              Vancouver and the surrounding Metro area offer excellent RV parks
              and campgrounds for both short and long stays. Below are five top
              choices:
            </p>
            <ol>
              <li
                ref={(el) => setSpotRef("best-locations-1", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Capilano River RV Park (West Vancouver)</strong>{" "}
                <Marker
                  coordY={49.32421}
                  coordX={-123.13213}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – Conveniently located near the Lions Gate Bridge, offering easy
                access to downtown, Stanley Park, and North Shore attractions.
                Full hookups, security, and family-friendly amenities.
              </li>
              <li
                ref={(el) => setSpotRef("best-locations-2", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Burnaby Cariboo RV Park (Burnaby)</strong>{" "}
                <Marker
                  coordY={49.24823}
                  coordX={-122.91238}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – A deluxe park near Burnaby Lake, featuring paved sites, an
                indoor pool, hot tub, and easy transit access to downtown
                Vancouver.
              </li>
              <li
                ref={(el) => setSpotRef("best-locations-3", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Dogwood Campgrounds &amp; RV Park (Surrey)</strong>{" "}
                <Marker
                  coordY={49.20817}
                  coordX={-122.80326}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – Nestled in a tranquil, forested area with quick highway access
                to both Vancouver and the Fraser Valley. Offers spacious sites
                and seasonal amenities like a heated pool.
              </li>
              <li
                ref={(el) => setSpotRef("best-locations-4", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Peace Arch RV Park (South Surrey)</strong>{" "}
                <Marker
                  coordY={49.07505}
                  coordX={-122.81369}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – Located near the U.S. border, featuring landscaped grounds,
                full hookups, and resort-like amenities. Close to White Rock’s
                beaches.
              </li>
              <li
                ref={(el) => setSpotRef("best-locations-5", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Fort Camping (Brae Island, Fort Langley)</strong>{" "}
                <Marker
                  coordY={49.17488}
                  coordX={-122.57763}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – Situated in historic Fort Langley along the Fraser River.
                Offers RV sites, tent cabins (seasonal), and proximity to Fort
                Langley National Historic Site, shops, and scenic trails.
              </li>
            </ol>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              Vancouver has specific parking and zoning rules for mobile homes
              and RVs. Overnight street parking for larger vehicles is generally
              not allowed, so plan to stay in designated parks or on private
              property with explicit permission. Some municipalities require
              permits for long-term stays, and zoning laws often prohibit
              long-term residence in RVs outside approved areas. Additionally:
            </p>
            <ul>
              <li>
                <strong>Driver’s License:</strong> A standard Class 5 license is
                usually enough for most rental RVs, unless they exceed certain
                weight limits or have air brakes.
              </li>
              <li>
                <strong>Safety:</strong> Check smoke/CO detectors, secure
                propane connections, and follow local fire bans.
              </li>
              <li>
                <strong>Hookups:</strong> Always use the correct connections for
                water, sewer, and electricity. Dump waste only at approved
                stations.
              </li>
              <li>
                <strong>Environment:</strong> Vancouver is known for its
                eco-conscious culture; properly dispose of trash, recycle, and
                minimize your carbon footprint.
              </li>
            </ul>

            {/* SECTION: Seasonal Availability */}
            <h2 id="seasonal-availability">
              Seasonal Availability & Considerations
            </h2>
            <p>
              Vancouver’s climate is relatively mild, making mobile home living
              feasible year-round, but the experience varies by season:
            </p>
            <ul>
              <li>
                <strong>Peak Season (Summer):</strong> High demand for RV sites
                from June to August; book early to secure spots.
              </li>
              <li>
                <strong>Off-Season (Winter):</strong> Many RV parks stay open,
                though some amenities may be limited. Prepare for rain and
                occasional snow; ensure your unit is winterized.
              </li>
              <li>
                <strong>Shoulder Seasons (Spring/Fall):</strong> Reduced crowds
                and moderate weather can make these ideal times to visit or
                arrange a longer stay at lower rates.
              </li>
            </ul>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Can I park my RV on Vancouver streets overnight?</h3>
              <p>
                Generally no. Oversized vehicles can’t park on city streets
                between 10 pm and 6 am, and local bylaws often prohibit sleeping
                in vehicles. Stick to designated RV parks or private
                driveways—always verify local regulations.
              </p>

              <h3>2. How much does it cost to rent an RV in Vancouver?</h3>
              <p>
                Rental costs vary by season and RV type. Expect anywhere from
                CA$50–$300 per night. Monthly pad fees for long-term stays in
                mobile home parks typically range from CA$350–$700, depending on
                location and amenities.
              </p>

              <h3>3. Do I need a special license or insurance?</h3>
              <p>
                A standard Class 5 driver’s license generally suffices. Rental
                companies often include insurance or offer it as an add-on.
                Always confirm the coverage details before driving an RV.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Renting a mobile home in Vancouver provides a flexible and
              cost-effective way to enjoy the city’s natural beauty and urban
              amenities. From RV parks offering full hookups near downtown to
              scenic campgrounds on the Fraser River, you’ll find diverse
              options for short or long stays. By following local regulations
              and best practices—and choosing the right location—you can make
              your mobile home experience in Vancouver both memorable and
              hassle-free.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}